import React from "react";
import { Box, Button, Typography, Grid, FormHelperText } from "@mui/material";
import {
	ArrowBack,
	RouterOutlined,
	WifiOutlined,
	HeadphonesOutlined,
} from "@mui/icons-material";
import dayjs from "dayjs";

import useGenerateReportForm from "./hooks/useGenerateReportForm";
import ReportPreview from "../ReportPreview";

import BasicConfigSection from "./sections/BasicConfigSection";
import TypeSelectorSection from "./sections/TypeSelectorSection";
import CpeSection from "./sections/CpeSection";
import OnuSection from "./sections/OnuSection";
import AtendimentosSection from "./sections/AtendimentosSection";
import MetricasSection from "./sections/MetricasSection";
import SlaRulesSection from "./sections/SlaRulesSection";
import TarifacaoSection from "./sections/TarifacaoSection";
import PeriodoSection from "./sections/PeriodoSection";
import ObservacoesSection from "./sections/ObservacoesSection";

// Lista de tipos de relatório disponíveis
const REPORT_TYPES = [
	{ id: "CPE", label: "CPE", IconComponent: RouterOutlined },
	{ id: "ONU", label: "ONU", IconComponent: WifiOutlined },
	{
		id: "Atendimentos",
		label: "Atendimentos",
		IconComponent: HeadphonesOutlined,
	},
];

// ==========================================
// COMPONENTE PRINCIPAL
// ==========================================
export default function GenerateReport(params) {
	const {
		setComponentValue,
		theme,
		styles,
		allHostsCliente,
		getInterface,
		loadingHost,
		loadingHostClient,
		loadingInterface,
		api,
		clienteInfo,
		selectedCliente,
		setSelectedCliente,
		clientes,
		setSnackbar,

		formState: externalFormState,
	} = params;

	const localFormState = useGenerateReportForm({
		api,
		clienteInfo,
		allHostsCliente,
		getInterface,
		setSnackbar,
	});

	const form = externalFormState ?? localFormState;

	const {
		selectedTypes,
		rules,
		selectors,
		allServiceClient,
		loadingServices,
		servicoHostOnus,
		periodoTipo,
		setPeriodoTipo,
		diasPersonalizado,
		setDiasPersonalizado,
		selectedAtendimentosServices,
		setSelectedAtendimentosServices,
		atendimentosInputValue,
		setAtendimentosInputValue,
		atendimentosIsOpen,
		setAtendimentosIsOpen,
		atendimentosSortedOptions,
		setAtendimentosSortedOptions,
		nomeRelatorio,
		setNomeRelatorio,
		dataInicial,
		setDataInicial,
		dataFinal,
		setDataFinal,
		openModal,
		setOpenModal,
		tarifacaoValores,
		setTarifacaoValores,
		metrics,
		isAgendamentoEnabled,
		setIsAgendamentoEnabled,
		agendamentoDataInicial,
		setAgendamentoDataInicial,
		emails,
		frequenciaAgendamento,
		setFrequenciaAgendamento,
		observacoes,
		setObservacoes,
		labelErp,
		showMetricas,
		showTarifacao,
		showDisponibilidade,
		allSelectedAtendimentos,
		displayedAtendimentosValue,
		handleToggle,
		handleAddEmail,
		handleRemoveEmail,
		handleEmailChange,
		generateCombinedOptions,
		handleOnuChange,
		handleAddOnu,
		handleRemoveOnu,
		getSortedOptions,
		handleAddRule,
		handleRemoveRule,
		handleRuleChange,
		handleToggleMetric,
		handleHostChange,
		handleInterfaceChange,
		addSelector,
		removeSelector,
		obterTextoAgendamento,
	} = form;

	const [validationFailed, setValidationFailed] = React.useState(false);

	const isCpeSelectionEmpty = selectedTypes.includes("CPE") &&
		(selectors.length === 0 || selectors.every((s) => !s.hostId || !s.interfaces || s.interfaces.length === 0));

	const isOnuSelectionEmpty = selectedTypes.includes("ONU") &&
		(servicoHostOnus.length === 0 || servicoHostOnus.every((s) => !s.onu || !s.host || !s.servico));

	const isMetricsSelectionEmpty = showMetricas && !Object.values(metrics || {}).some(Boolean);

	// ==========================================
	// GERAR RELATÓRIO (ação principal)
	// ==========================================
	const handleGerarRelatorio = async () => {
		if (isCpeSelectionEmpty || isOnuSelectionEmpty) {
			setValidationFailed(true);
			setSnackbar?.({
				children: "Por favor, preencha as seleções obrigatórias de CPE/ONU antes de gerar o relatório.",
				severity: "error",
			});
			return;
		}
		if (isMetricsSelectionEmpty) {
			setValidationFailed(true);
			setSnackbar?.({
				children: "Por favor, selecione pelo menos uma Métrica e Tarifação antes de gerar o relatório.",
				severity: "error",
			});
			return;
		}
		setValidationFailed(false);
		try {
			setOpenModal(true);

			const bodyCreate = {
				...(params.reportToEdit ? { id: params.reportToEdit.id } : {}),
				nome: nomeRelatorio,
				clienteId: clienteInfo?.id,
				observacoes: observacoes || null,
				tipos: selectedTypes.map((t) =>
					t === "Atendimentos" ? "ATENDIMENTO" : t.toUpperCase(),
				),
				dataInicial: isAgendamentoEnabled ? null : dataInicial,
				dataFinal: isAgendamentoEnabled ? null : dataFinal,
				agendamento: isAgendamentoEnabled
					? {
							enabled: true,
							frequencia: frequenciaAgendamento.toUpperCase(),
							dataInicial: agendamentoDataInicial,
							tipoPeriodo: periodoTipo.toUpperCase(),
							diasPersonalizado:
								periodoTipo === "Personalizado"
									? parseInt(diasPersonalizado)
									: null,
							emails: emails.map((e) => e.value).filter(Boolean),
						}
					: { enabled: false },
				metricas: {
					trafego: metrics.trafego,
					tarifacao: metrics.tarifacao,
					disponibilidade: metrics.disponibilidade,
					perdaPacote: metrics.perdaPacote,
					latencia: metrics.latencia,
				},
				motorDeRegras: rules.filter((r) => r.value !== "" && r.value != null),
				tarifacao: metrics.tarifacao
					? {
							valorPorMbps: parseFloat(tarifacaoValores.valorPorMbps),
							planoContratadoMbps: parseFloat(
								tarifacaoValores.planoContratadoMbps,
							),
							valorMbpsExcedente: parseFloat(
								tarifacaoValores.valorMbpsExcedente,
							),
						}
					: null,
				cpe: selectedTypes.includes("CPE")
					? {
							hosts: selectors
								.filter((s) => s.hostId)
								.map((s) => ({
									zabbixHostId: s.hostId,
									interfaces: s.interfaces,
								})),
						}
					: null,
				onu: selectedTypes.includes("ONU")
					? {
							itens: servicoHostOnus
								.filter((s) => s?.onu?.onuItem)
								.map((s) => ({ zabbixItemId: s.onu.onuItem })),
						}
					: null,
				atendimentos: selectedTypes.includes("Atendimentos")
					? {
							contratoIds: selectedAtendimentosServices.map(
								(s) => s.id_cliente_servico,
							),
						}
					: null,
			};

			const createResponse = await api.post(
				params.reportToEdit
					? "/interno/relatorio/update"
					: "/interno/relatorio/create",
				bodyCreate,
			);
			const relatorioId = params.reportToEdit
				? params.reportToEdit.id
				: createResponse?.data?.id;

			if (isAgendamentoEnabled) {
				setSnackbar?.({
					children: params.reportToEdit ? "Agendamento atualizado com sucesso" : "Relatório agendado com sucesso",
					severity: "success",
				});
				setComponentValue(0);
				return;
			}

			// Geração em background: o backend coleta direto no banco do Zabbix (em
			// chunks) e monta o PDF; a tabela mostra o progresso e libera o download
			// ao concluir — pode sair da página à vontade
			const generateResponse = await api.post("/interno/relatorio/generate", {
				id: relatorioId,
			});

			setSnackbar?.({
				children:
					generateResponse?.data?.message ||
					"Relatório na fila de geração — acompanhe o progresso na tabela.",
				severity: "success",
			});
			setComponentValue(0);
		} catch (error) {
			console.error(error);
			// 409 = o relatório já está na fila/em geração: o cadastro deu certo,
			// então volta para a lista acompanhar o progresso
			const jaEmGeracao = error?.response?.status === 409;
			setSnackbar?.({
				children:
					error?.response?.data?.message ||
					"Falha ao iniciar a geração do relatório",
				severity: jaEmGeracao ? "warning" : "error",
			});
			if (jaEmGeracao) setComponentValue(0);
		} finally {
			setOpenModal(false);
		}
	};

	const isSentAgendado =
		params.reportToEdit?.status === "AGENDADO" &&
		params.reportToEdit?.agendamento?.dataInicial &&
		dayjs(params.reportToEdit.agendamento.dataInicial).isBefore(dayjs());

	const isFormDisabled =
		openModal ||
		isSentAgendado ||
		!nomeRelatorio ||
		selectedTypes.length === 0 ||
		(!isAgendamentoEnabled && (!dataInicial || !dataFinal));

	return (
		<Box
			sx={{
				flexGrow: 1,
				p: 1,
				bgcolor: theme.palette?.background?.paper ?? "#fff",
				borderRadius: "12px",
			}}
		>
			{/* CABEÇALHO STICKY */}
			<Box
				sx={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					position: "sticky",
					top: "52.5px",
					zIndex: 100,
					bgcolor: theme.palette?.background?.paper ?? "#fff",
					pb: 2,
					pt: 1,
				}}
			>
				<Box sx={{ mb: 2, p: 1, pb: 0 }}>
					<Button
						size="small"
						variant="text"
						startIcon={<ArrowBack />}
						onClick={() => setComponentValue?.(0)}
						sx={{ color: "text.secondary" }}
					>
						Voltar para relatórios
					</Button>
					<Typography variant="h5" sx={{ fontWeight: "bold" }}>
						{params.reportToEdit ? "Editar Relatório" : "Criar Relatório"}
					</Typography>
				</Box>

				<Button
					variant="contained"
					size="small"
					onClick={handleGerarRelatorio}
					disabled={isFormDisabled}
				>
					{openModal
						? "Gerando..."
						: params.reportToEdit
							? "Salvar e Gerar Relatório"
							: "Gerar Relatório"}
				</Button>
			</Box>

			<Grid container spacing={1}>
				{/* COLUNA ESQUERDA: Formulário */}
				<Grid item xs={12} md={6}>
					<Box
						sx={{
							p: 2,
							bgcolor: theme.palette?.background?.wallpaper ?? "#f5f5f5",
							borderRadius: "12px",
							display: "flex",
							flexDirection: "column",
							gap: 1,
						}}
					>
						<BasicConfigSection
							theme={theme}
							styles={styles}
							nomeRelatorio={nomeRelatorio}
							setNomeRelatorio={setNomeRelatorio}
							isAgendamentoEnabled={isAgendamentoEnabled}
							setIsAgendamentoEnabled={setIsAgendamentoEnabled}
							frequenciaAgendamento={frequenciaAgendamento}
							setFrequenciaAgendamento={setFrequenciaAgendamento}
							agendamentoDataInicial={agendamentoDataInicial}
							setAgendamentoDataInicial={setAgendamentoDataInicial}
							emails={emails}
							handleAddEmail={handleAddEmail}
							handleRemoveEmail={handleRemoveEmail}
							handleEmailChange={handleEmailChange}
							obterTextoAgendamento={obterTextoAgendamento}
							clientes={clientes}
							selectedCliente={selectedCliente}
							setSelectedCliente={setSelectedCliente}
						/>

						<TypeSelectorSection
							theme={theme}
							selectedTypes={selectedTypes}
							handleToggle={handleToggle}
							reportTypes={REPORT_TYPES}
						/>

						{selectedTypes?.includes("CPE") && (
							<Box>
								<CpeSection
									theme={theme}
									selectors={selectors}
									allHostsCliente={allHostsCliente}
									handleHostChange={handleHostChange}
									handleInterfaceChange={handleInterfaceChange}
									addSelector={addSelector}
									removeSelector={removeSelector}
									loadingHost={loadingHost}
									loadingHostClient={loadingHostClient}
									loadingInterface={loadingInterface}
								/>
								{validationFailed && isCpeSelectionEmpty && (
									<FormHelperText error sx={{ mt: 1, ml: 2, fontSize: "14px", fontWeight: "bold" }}>
										Por favor, selecione pelo menos um Host e uma Interface.
									</FormHelperText>
								)}
							</Box>
						)}

						{selectedTypes?.includes("ONU") && (
							<Box>
								<OnuSection
									theme={theme}
									labelErp={labelErp}
									servicoHostOnus={servicoHostOnus}
									handleOnuChange={handleOnuChange}
									handleAddOnu={handleAddOnu}
									handleRemoveOnu={handleRemoveOnu}
									generateCombinedOptions={generateCombinedOptions}
									loadingServices={loadingServices}
								/>
								{validationFailed && isOnuSelectionEmpty && (
									<FormHelperText error sx={{ mt: 1, ml: 2, fontSize: "14px", fontWeight: "bold" }}>
										Por favor, selecione o Serviço, Host e ONU.
									</FormHelperText>
								)}
							</Box>
						)}

						{selectedTypes?.includes("Atendimentos") && (
							<AtendimentosSection
								theme={theme}
								labelErp={labelErp}
								selectedAtendimentosServices={selectedAtendimentosServices}
								setSelectedAtendimentosServices={
									setSelectedAtendimentosServices
								}
								atendimentosSortedOptions={atendimentosSortedOptions}
								setAtendimentosSortedOptions={setAtendimentosSortedOptions}
								atendimentosIsOpen={atendimentosIsOpen}
								setAtendimentosIsOpen={setAtendimentosIsOpen}
								atendimentosInputValue={atendimentosInputValue}
								setAtendimentosInputValue={setAtendimentosInputValue}
								allServiceClient={allServiceClient}
								allSelectedAtendimentos={allSelectedAtendimentos}
								displayedAtendimentosValue={displayedAtendimentosValue}
								getSortedOptions={getSortedOptions}
								loadingServices={loadingServices}
							/>
						)}

						{showMetricas && (
							<Box>
								<MetricasSection
									theme={theme}
									styles={styles}
									metrics={metrics}
									handleToggleMetric={handleToggleMetric}
									showTarifacao={showTarifacao}
									showDisponibilidade={showDisponibilidade}
									rules={rules}
								/>
								{validationFailed && isMetricsSelectionEmpty && (
									<FormHelperText error sx={{ mt: 1, ml: 2, fontSize: "14px", fontWeight: "bold" }}>
										Por favor, selecione pelo menos uma Métrica e Tarifação.
									</FormHelperText>
								)}
							</Box>
						)}

						{metrics?.disponibilidade && (
							<SlaRulesSection
								theme={theme}
								rules={rules}
								handleAddRule={handleAddRule}
								handleRemoveRule={handleRemoveRule}
								handleRuleChange={handleRuleChange}
								selectedTypes={selectedTypes}
							/>
						)}

						{metrics?.tarifacao && (
							<TarifacaoSection
								theme={theme}
								tarifacaoValores={tarifacaoValores}
								setTarifacaoValores={setTarifacaoValores}
							/>
						)}

						<PeriodoSection
							theme={theme}
							isAgendamentoEnabled={isAgendamentoEnabled}
							dataInicial={dataInicial}
							setDataInicial={setDataInicial}
							dataFinal={dataFinal}
							setDataFinal={setDataFinal}
							periodoTipo={periodoTipo}
							setPeriodoTipo={setPeriodoTipo}
							diasPersonalizado={diasPersonalizado}
							setDiasPersonalizado={setDiasPersonalizado}
						/>

						<ObservacoesSection
							theme={theme}
							observacoes={observacoes}
							setObservacoes={setObservacoes}
						/>
					</Box>
				</Grid>

				{/* COLUNA DIREITA: Preview */}
				<Grid item xs={12} md={6}>
					{/* A caixa visual fica dentro do Grid item, assim como na esquerda */}
					<Box
						sx={{
							p: 2,
							bgcolor: theme.palette?.background?.wallpaper ?? "#f5f5f5",
							borderRadius: "12px",
							display: "flex",
							flexDirection: "column",
							gap: 1,
							height: "100%", // Garante que a caixa preencha toda a altura disponível se o form da esquerda for maior
						}}
					>
						<ReportPreview
							states={form}
							clienteInfo={clienteInfo}
							theme={theme}
							api={api}
						/>
					</Box>
				</Grid>
			</Grid>
		</Box>
	);
}
